import React from 'react';
import {
  adjustForCPMById,
  calcEffectivePrice,
  calcEffectiveUnitPrice,
  calcEstimatedUnits,
  calcGrossUnitPrice,
  calcMediaBudget,
  calcPlannedMargin
} from '@/features/line-items/line-item-form/utils/line-item-form-utils';
import { isNil } from 'lodash';
import { formatCurrency } from '@/components/tables/table-utils';
import { formatPercentage, getUnitName } from 'shared/src/domain/line-item/line-item-utils';
import { Button } from '@/components/forms/ui/button';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons';
import {
  AgencyMarkup,
  Bold,
  CalculatedField,
  EffectivePrice,
  GrossPrice,
  isAboveZero,
  ReferralFee
} from '@/features/line-items/line-item-form/ui/calculated-fields-components';

type CalculationInput = {
  price: number | undefined;
  unit_price: number | undefined;
  unit_price_display?: number | undefined;
  unit_price_type_id: number | undefined;
  planned_unit_cost: number | undefined;
  agency_markup_rate: number | undefined;
  referral_commission_rate: number | undefined;
};

type Props = {
  inputs: CalculationInput;
  isLocked?: boolean;
  onLockMargin?: () => void;
};

export function StandardLineItemCalculatedFields({ inputs, isLocked, onLockMargin }: Props) {
  const { price, unit_price, planned_unit_cost, agency_markup_rate, referral_commission_rate } =
    inputs;

  if (isNil(price) && isNil(unit_price) && isNil(planned_unit_cost)) return null;

  return (
    <div className="flex flex-col gap-2">
      <GrossPrice price={price} agency_markup_rate={agency_markup_rate} />
      <AgencyMarkup price={price} agency_markup_rate={agency_markup_rate} />
      <EffectivePrice price={price} referral_commission_rate={referral_commission_rate} />
      <ReferralFee price={price} referral_commission_rate={referral_commission_rate} />
      <GrossUnitPrice
        unit_price={inputs.unit_price_display ?? unit_price}
        agency_markup_rate={agency_markup_rate}
      />
      <EffectiveUnitPrice
        unit_price={unit_price}
        referral_commission_rate={referral_commission_rate}
      />
      <EstimatedUnits inputs={inputs} />
      <LockablePlannedMargin
        planned_unit_cost={planned_unit_cost}
        unit_price={unit_price}
        referral_commission_rate={referral_commission_rate}
        isLocked={isLocked}
        onLockMargin={onLockMargin}
      />
    </div>
  );
}

export function StandardLineItemMediaTeamCalculatedFields({
  inputs,
  isLocked,
  onLockMargin
}: Props) {
  const { price, unit_price, planned_unit_cost, referral_commission_rate, unit_price_type_id } =
    inputs;

  return (
    <div className="flex flex-col gap-2">
      <UnitPrice unit_price={unit_price} unit_price_type_id={unit_price_type_id} />
      <EffectivePrice price={price} referral_commission_rate={referral_commission_rate} />
      <LockablePlannedMargin
        planned_unit_cost={planned_unit_cost}
        unit_price={unit_price}
        referral_commission_rate={referral_commission_rate}
        isLocked={isLocked}
        onLockMargin={onLockMargin}
      />
      <EstimatedUnits inputs={inputs} />
      <MediaBudget inputs={inputs} />
    </div>
  );
}

type GrossUnitPriceProps = {
  unit_price: number | undefined;
  agency_markup_rate: number | undefined;
};

function GrossUnitPrice({ unit_price, agency_markup_rate }: GrossUnitPriceProps) {
  const gross_unit_price = calcGrossUnitPrice(unit_price, agency_markup_rate);
  if (isNil(gross_unit_price) || isNil(unit_price) || !isAboveZero(agency_markup_rate)) return null;
  return (
    <CalculatedField
      label="Gross Unit Price"
      detail={formatCurrency(gross_unit_price)}
      tooltip={
        <>
          Gross Unit Price calculated as <Bold>{formatCurrency(unit_price)}</Bold> Unit Price / (1 -{' '}
          <Bold>{formatPercentage(agency_markup_rate)}</Bold> Agency Markup)
        </>
      }
    />
  );
}

type EffectiveUnitPriceProps = {
  unit_price: number | undefined;
  referral_commission_rate: number | undefined;
};

function EffectiveUnitPrice({ unit_price, referral_commission_rate }: EffectiveUnitPriceProps) {
  const value = calcEffectiveUnitPrice(unit_price, referral_commission_rate);
  if (isNil(value) || isNil(unit_price) || !isAboveZero(referral_commission_rate)) return null;
  return (
    <CalculatedField
      label="Effective Unit Price"
      detail={formatCurrency(value)}
      tooltip={
        <>
          <div>
            Effective Unit Price calculated as <Bold>{formatCurrency(unit_price)}</Bold> Unit Price
            × (1 - <Bold>{formatPercentage(referral_commission_rate)}</Bold> Referral Rate)
          </div>
          <div>
            The default Unit Price is grossed up so that Effective Unit Price matches the Rate Card
            to maintain the Rate Card Target Margin.
          </div>
        </>
      }
    />
  );
}

function EstimatedUnits({ inputs }: { inputs: CalculationInput }) {
  const { unitPriceTypes } = useChannelRatesContext();
  const { price, unit_price, unit_price_type_id, referral_commission_rate } = inputs;
  const unit_price_type = unitPriceTypes.find(type => type.id === unit_price_type_id);
  const unitLabel = unit_price_type ? getUnitName(unit_price_type.name).full : undefined;
  const effective_price = calcEffectivePrice(price, referral_commission_rate);
  const effective_unit_price = calcEffectiveUnitPrice(unit_price, referral_commission_rate);
  const estimated_units = calcEstimatedUnits(effective_price, effective_unit_price);

  if (isNil(estimated_units) || isNil(price) || isNil(unit_price) || isNil(unit_price_type))
    return null;

  const unitType = unit_price_type_id === 1 ? ' × 1,000 [CPM]' : '';
  const isReferralCommission = isAboveZero(referral_commission_rate);

  return (
    <CalculatedField
      label="Estimated Units"
      detail={
        <div className="flex items-center gap-2">
          <Bold>{estimated_units.toLocaleString()}</Bold>
          <span className="text-xs capitalize text-gray-600">{unitLabel}</span>
        </div>
      }
      tooltip={
        <>
          Estimated Units calculated as <Bold>{formatCurrency(price)}</Bold>{' '}
          {isReferralCommission && 'Effective '}Price / <Bold>{formatCurrency(unit_price)}</Bold>{' '}
          {isReferralCommission && 'Effective '}Unit Price
          {unitType}
        </>
      }
    />
  );
}

type LockablePlannedMarginProps = {
  planned_unit_cost: number | undefined;
  unit_price: number | undefined;
  referral_commission_rate: number | undefined;
  isLocked?: boolean;
  onLockMargin?: () => void;
};

function LockablePlannedMargin({
  planned_unit_cost,
  unit_price,
  referral_commission_rate,
  isLocked,
  onLockMargin
}: LockablePlannedMarginProps) {
  const planned_margin = calcPlannedMargin(planned_unit_cost, unit_price, referral_commission_rate);
  if (isNil(planned_margin) || isNil(unit_price) || isNil(planned_unit_cost)) return null;
  const hasReferral = isAboveZero(referral_commission_rate);
  const effective_unit_price = unit_price * (1 - (referral_commission_rate ?? 0));
  const priceLabel = hasReferral ? 'Effective Unit Price' : 'Unit Price';
  const priceValue = hasReferral ? effective_unit_price : unit_price;
  return (
    <div className="flex w-full gap-2">
      <CalculatedField
        label="Planned Margin"
        detail={formatPercentage(planned_margin)}
        tooltip={
          <>
            Planned Margin calculated as (<Bold>{formatCurrency(priceValue)}</Bold> {priceLabel} -{' '}
            <Bold>{formatCurrency(planned_unit_cost)}</Bold> Planned Unit Cost) /{' '}
            <Bold>{formatCurrency(priceValue)}</Bold> {priceLabel} × 100
          </>
        }
      />
      {!isNil(isLocked) && onLockMargin && (
        <Button variant="outline" onClick={onLockMargin}>
          {isLocked && <FontAwesomeIcon icon={faLock} className="mr-1" />}
          <div>{isLocked ? 'Unlock' : 'Lock'}</div>
        </Button>
      )}
    </div>
  );
}

type MediaBudgetProps = {
  inputs: CalculationInput;
};

function MediaBudget({ inputs }: MediaBudgetProps) {
  const { price, unit_price, planned_unit_cost, referral_commission_rate } = inputs;
  const planned_margin = calcPlannedMargin(planned_unit_cost, unit_price, referral_commission_rate);
  const media_budget = calcMediaBudget(price, planned_margin, referral_commission_rate);
  if (isNil(media_budget) || isNil(price) || isNil(planned_margin)) return null;
  const hasReferral = isAboveZero(referral_commission_rate);
  const effective_price = price * (1 - (referral_commission_rate ?? 0));
  const priceLabel = hasReferral ? 'Effective Price' : 'Price';
  const priceValue = hasReferral ? effective_price : price;

  return (
    <CalculatedField
      label="Media Budget"
      detail={formatCurrency(media_budget)}
      tooltip={
        <>
          Media Budget calculated as <Bold>{formatCurrency(priceValue)}</Bold> {priceLabel} × (1 -{' '}
          <Bold>{formatPercentage(planned_margin)}</Bold> Planned Margin)
        </>
      }
    />
  );
}

type UnitPriceProps = {
  unit_price: number | undefined;
  unit_price_type_id: number | undefined;
};

function UnitPrice({ unit_price, unit_price_type_id }: UnitPriceProps) {
  if (isNil(unit_price)) return null;
  return (
    <CalculatedField
      label={`Unit Price ${unit_price_type_id === 1 ? ' (CPM)' : ''}`}
      detail={formatCurrency(adjustForCPMById(unit_price, unit_price_type_id))}
      tooltip={
        <>
          Unit Price is <span className="font-bold">entered above</span> for Standard line items
        </>
      }
    />
  );
}
